import DOMPurify from 'dompurify';
import type { Config, UponSanitizeAttributeHook } from 'dompurify';

export interface HtmlPurifier {
  isSupported: boolean;
  sanitize(dirty: string, config: Config): string;
  addHook(entryPoint: 'uponSanitizeAttribute', hook: UponSanitizeAttributeHook): void;
}

export type HtmlSanitizer = (html: string) => string;

const PRODUCT_HTML_CONFIG: Config = {
  ALLOWED_TAGS: [
    'p', 'div', 'span', 'br', 'hr',
    'strong', 'b', 'em', 'i', 'u', 's', 'small', 'sup', 'sub',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
    'a',
  ],
  ALLOWED_ATTR: ['dir', 'style', 'width', 'colspan', 'rowspan', 'href'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  ADD_URI_SAFE_ATTR: ['dir', 'width', 'colspan', 'rowspan'],
};

const ALLOWED_STYLE_PROPERTIES = new Set([
  'width', 'height', 'min-width', 'max-width',
  'text-align', 'vertical-align',
  'font-weight', 'font-style', 'font-size', 'text-decoration',
  'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right',
  'padding', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right',
  'border', 'border-collapse', 'border-spacing',
  'white-space', 'line-height', 'letter-spacing',
]);

const SAFE_STYLE_VALUE = /^[\w\s.%#,-]+$/;

export const filterStyleDeclarations = (value: string): string => {
  const declarations = value.split(';').map((d) => d.trim()).filter(Boolean);
  const kept = declarations.filter((declaration) => {
    const separator = declaration.indexOf(':');
    if (separator < 0) return false;
    const property = declaration.slice(0, separator).trim().toLowerCase();
    const propertyValue = declaration.slice(separator + 1).trim();
    return ALLOWED_STYLE_PROPERTIES.has(property) && SAFE_STYLE_VALUE.test(propertyValue);
  });
  if (kept.length === declarations.length) return value;
  return kept.join('; ');
};

const restrictStyleAttribute: UponSanitizeAttributeHook = (_node, event) => {
  if (event.attrName !== 'style') return;
  const filtered = filterStyleDeclarations(event.attrValue);
  if (!filtered.trim()) {
    event.keepAttr = false;
    return;
  }
  event.attrValue = filtered;
};

export const createProductHtmlSanitizer = (purify: HtmlPurifier): HtmlSanitizer => {
  if (!purify.isSupported) {
    throw new Error('HTML sanitization is not supported in this environment');
  }
  purify.addHook('uponSanitizeAttribute', restrictStyleAttribute);
  return (html) => {
    if (typeof html !== 'string' || html === '') return '';
    return String(purify.sanitize(html, PRODUCT_HTML_CONFIG));
  };
};

let browserSanitizer: HtmlSanitizer | null = null;

export const sanitizeProductHtml: HtmlSanitizer = (html) => {
  browserSanitizer ??= createProductHtmlSanitizer(DOMPurify);
  return browserSanitizer(html);
};
